import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, ModalUI, Switch, Tabs, useQueryFetch } from 'sonata-react-components';

import type { DealInterface } from 'sonata-static-storage/lib/schemas/premium/deal.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

import './dealsManager.scss';

import { getDealsPublishers } from '@/services/deals.service';
import { AssignDealsModal } from '../AssignDealsModal';
import { DealsTable } from './DealsTable';
import { NewDeal } from './NewDeal/NewDeal';

import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

type Props = {
  onClose: () => void;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

type State = {
  view: 'list' | 'createDeal' | 'assignDeals';
  editedDeal?: Partial<DealInterface<string>>;
};

export function DealsManager({ onClose, user, userRoles }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const modalRef = useRef<React.ElementRef<typeof ModalUI>>(null);
  const [{ editedDeal, view }, setState] = useState<State>({
    editedDeal: undefined,
    view: 'list'
  });
  const dealsPublishersToken: Record<string, any> = {};

  useEffect(() => {
    return () => {
      dealsPublishersToken.cancelRequest?.();
    };
  }, []);

  const setView = (newView: State['view']): void => {
    setState(prev => ({
      ...prev,
      view: newView
    }));
  };

  const setEditedDeal = (deal?: Partial<DealInterface<string>>): void => {
    setState(prev => ({
      ...prev,
      editedDeal: deal
    }));
  };

  const BASE_ID = 'dealsManager';

  const handleAssignDealClick = (): void => {
    setView('assignDeals');
  };

  const handleCreateDealClick = (): void => {
    setView('createDeal');
  };

  useEffect(() => {
    modalRef?.current?.openModal();
  }, [modalRef]);

  const handleOnCloseModal = () => {
    if (view !== 'list') {
      setView('list');
    } else {
      onClose();
    }
  };

  const { data: dealsPublishers } = useQueryFetch(['dealsPublishers'], async () => {
    const resultDealsPublishers = await getDealsPublishers(dealsPublishersToken);
    return resultDealsPublishers.items;
  });

  const tabs = [
    {
      component: (
        <DealsTable
          dealsPublishers={dealsPublishers}
          editedDeal={editedDeal}
          onlyUserDeals={true}
          setEditedDeal={setEditedDeal}
        />
      ),
      description: formatMessage({
        defaultMessage: 'These are all the deals assigned to your account.',
        id: 'menu.dealsManager.tab.yourDealsDescription'
      }),
      id: 'your_deals',
      title: formatMessage({
        defaultMessage: 'Your Deals',
        id: 'menu.dealsManager.tab.yourDeals'
      })
    },
    {
      component: <DealsTable dealsPublishers={dealsPublishers} editedDeal={editedDeal} setEditedDeal={setEditedDeal} />,
      description: formatMessage({
        defaultMessage: 'These are all the deals assigned to other accounts.',
        id: 'menu.dealsManager.tab.allDealsDescription'
      }),
      id: 'all_deals',
      title: formatMessage({
        defaultMessage: 'All Deals',
        id: 'menu.dealsManager.tab.allDeals'
      })
    }
  ];

  return (
    <>
      <ModalUI
        className={cn('dealsManager__modal', { 'dealsManager__modal--hide': view !== 'list' })}
        closeModalCallback={handleOnCloseModal}
        description={formatMessage({
          defaultMessage:
            'This section displays the deals assigned to your account and to others, where you can easily enable or disable them, create new deals and reassign them to others',
          id: 'menu.dealsManager.description'
        })}
        ref={modalRef}
        title={formatMessage({
          defaultMessage: 'Deals Manager',
          id: 'menu.dealsManager.title'
        })}
        width="1300px"
      >
        <div className="dealsManager">
          <Tabs
            className="dealsManager__tabs"
            hideNoneActivePanels
            initialTab={tabs[0].id}
            shape="narrow"
            tabs={tabs}
            variant="filled"
          />
          <footer className="dealsManager__footer">
            <ButtonUI
              icon="fa-regular fa-hand-holding-star"
              id={`${BASE_ID}_multipleExportBtn`}
              onClick={handleAssignDealClick}
              size="medium"
              text={formatMessage({
                defaultMessage: 'Assign Deals',
                id: 'menu.routes.assignDealsModal'
              })}
              type="secondary"
            />
            <ButtonUI
              icon="fa-regular fa-plus"
              id={`${BASE_ID}_createDealBtn`}
              onClick={handleCreateDealClick}
              size="medium"
              text={formatMessage({
                defaultMessage: 'Create Deal',
                id: 'menu.dealsManager.createDeal'
              })}
              type="primary"
            />
          </footer>
        </div>
      </ModalUI>
      <Switch>
        <Switch.Case condition={!!editedDeal || view === 'createDeal'}>
          <NewDeal
            deal={editedDeal}
            isModalOpened={!!editedDeal || view === 'createDeal'}
            onClose={() => {
              setEditedDeal(undefined);
              setView('list');
            }}
            onSave={() => {
              setEditedDeal(undefined);
              setView('list');
            }}
            user={user}
          />
        </Switch.Case>
        <Switch.Case condition={view === 'assignDeals'}>
          <AssignDealsModal isOpen onClose={() => setView('list')} user={user} userRoles={userRoles} />
        </Switch.Case>
      </Switch>
    </>
  );
}
